import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Church,
  DoorOpen,
  Drum,
  Heart,
  Home,
  MapPin,
  MessageCircle,
  Music,
  Phone,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import SEO from "../components/SEO";
import ServiceAreasHub from "../components/ServiceAreasHub";
import WhatsAppButton from "../components/WhatsAppButton";
import { CONTACT, BRAND_NAME } from "../data/contactInfo";
import { SITE_NAME } from "../data/seoData";
import {
  PAGE_SEO,
  SEO_FAQ,
  getLocalBusinessSchema,
  getFaqSchema,
} from "../data/seoData";
import { SEO_IMAGES } from "../data/seoImages";
import { publicImage, WEDDING_IMAGES } from "../data/pageImages";
import "../styles/WeddingPage.css";

const CONTACT_SEARCH = { pathname: "/contact", search: "?type=wedding&form=open" };

const PERFORMANCE_SHOWCASE = [
  { src: WEDDING_IMAGES.ensemble, alt: "Live Chenda Melam ensemble" },
  { src: WEDDING_IMAGES.groomEntry, alt: "Procession and entry performance" },
  { src: WEDDING_IMAGES.ceremony, alt: "Wedding celebration melam" },
  { src: WEDDING_IMAGES.reception, alt: "Reception and hall performance" },
  { src: WEDDING_IMAGES.night, alt: "Evening wedding ensemble" },
  { src: WEDDING_IMAGES.temple, alt: "Temple and cultural stage" },
];

const WHY_BOOK = [
  {
    icon: Drum,
    title: "Professional artists",
    text: "Led by Thiruvambady Vineesh Maarar — 30+ years in Kerala temple melam and wedding performances.",
  },
  {
    icon: CheckCircle2,
    title: "Clear packages",
    text: "From compact wedding-entry ensembles to full Panchari Melam — transparent pricing for every budget.",
  },
  {
    icon: MapPin,
    title: "All Chennai covered",
    text: "We travel across Chennai and South India — punctual, professionally dressed artists for every venue.",
  },
];

const OCCASIONS = [
  {
    icon: DoorOpen,
    title: "Wedding Entry Chenda Melam",
    desc: "Grand traditional entrance for your marriage ceremony and muhurtham.",
  },
  {
    icon: Users,
    title: "Groom Entry Chenda Melam",
    desc: "Powerful Kerala melam to welcome the groom — baraat-style procession.",
  },
  {
    icon: Heart,
    title: "Bride Entry Chenda Melam",
    desc: "Elegant traditional Chenda ensemble for the bride's ceremonial entry.",
  },
  {
    icon: Music,
    title: "Baraat Chenda Melam",
    desc: "Energetic live percussion for wedding baraat and procession routes.",
  },
  {
    icon: Sparkles,
    title: "Reception Chenda Melam",
    desc: "Premium Kerala Chenda Melam for wedding receptions and evening celebrations.",
  },
  {
    icon: Home,
    title: "Housewarming Chenda Melam",
    desc: "Auspicious Chenda Melam for griha pravesh and new home events.",
  },
  {
    icon: Church,
    title: "Temple Festival Chenda Melam",
    desc: "Authentic Panchari Melam and temple melam for utsavams across Tamil Nadu.",
  },
  {
    icon: Star,
    title: "Corporate & Inauguration",
    desc: "Professional Kerala traditional music for launches and cultural evenings.",
  },
];

const BOOKING_STEPS = [
  {
    icon: MessageCircle,
    title: "Share your details",
    text: "Date, venue area, and occasion — WhatsApp or the booking form.",
  },
  {
    icon: Calendar,
    title: "Get a clear quote",
    text: "Team size, duration, and travel priced transparently for your event.",
  },
  {
    icon: Users,
    title: "Confirm the ensemble",
    text: "We lock artists, repertoire, and cue timings for your rituals.",
  },
  {
    icon: Drum,
    title: "Live on your day",
    text: "Artists arrive dressed, tuned, and ready for a powerful entry.",
  },
];

const EXPLORE_LINKS = [
  { to: "/melams", label: "Melam forms" },
  { to: "/programs", label: "Programs" },
  { to: "/fusions", label: "Fusion Melam" },
  { to: "/gallery", label: "Gallery" },
  { to: "/chenda-classes-chennai", label: "Classes" },
  { to: "/testimonials", label: "Testimonials" },
];

const ChendaMelamChennai = () => (
  <>
    <SEO
      title={PAGE_SEO.wedding.title}
      description={PAGE_SEO.wedding.description}
      path={PAGE_SEO.wedding.path}
      ogImage={SEO_IMAGES.wedding}
      jsonLd={{
        "@context": "https://schema.org",
        "@graph": [getLocalBusinessSchema(), getFaqSchema()],
      }}
    />

    <main className="wedding-page">
      <header className="wedding-hero">
        <img
          className="wedding-hero__media"
          src={publicImage(WEDDING_IMAGES.hero)}
          alt="Kerala Chenda Melam ensemble performing for a wedding celebration"
          width={1600}
          height={900}
          fetchPriority="high"
        />
        <div className="wedding-hero__veil" aria-hidden="true" />
        <div className="wedding-hero__content">
          <p className="wedding-hero__brand">{BRAND_NAME}</p>
          <h1 className="wedding-hero__title">Chenda Melam for Wedding in Chennai</h1>
          <p className="wedding-hero__lead">
            Authentic Kerala Chenda Melam for groom entry, baraat, reception, and muhurtham —
            across Chennai, Tamil Nadu, Andhra Pradesh, and Telangana.
          </p>
          <div className="wedding-hero__actions">
            <WhatsAppButton>WhatsApp Booking</WhatsAppButton>
            <Link to={CONTACT_SEARCH} className="wedding-btn wedding-btn--ghost">
              Get a Quote
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      <section className="wedding-section wedding-why" aria-labelledby="wedding-why-heading">
        <div className="wedding-section__inner">
          <p className="wedding-kicker">Why families choose us</p>
          <h2 id="wedding-why-heading" className="wedding-heading">
            Kerala thunder, wedding-ready
          </h2>
          <p className="wedding-lead">
            {SITE_NAME} brings Thrissur Pooram–level artistry to your marriage — specialists in
            Kerala style traditional Chenda Melam, not a generic wedding band.
          </p>
          <ul className="wedding-why__list">
            {WHY_BOOK.map(({ icon: Icon, title, text }) => (
              <li className="wedding-why__item" key={title}>
                <span className="wedding-icon" aria-hidden="true">
                  <Icon size={22} strokeWidth={1.75} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="wedding-section wedding-occasions" aria-labelledby="wedding-occasions-heading">
        <div className="wedding-section__inner">
          <p className="wedding-kicker">Services</p>
          <h2 id="wedding-occasions-heading" className="wedding-heading">
            Wedding Chenda Melam moments
          </h2>
          <p className="wedding-lead">
            Traditional Kerala Chenda Melam for every ritual — tailored to your venue, guest count,
            and family traditions.
          </p>
          <ul className="wedding-occasions__grid">
            {OCCASIONS.map(({ icon: Icon, title, desc }) => (
              <li className="wedding-occasion" key={title}>
                <span className="wedding-icon wedding-icon--soft" aria-hidden="true">
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <h3>{title}</h3>
                <p>{desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="wedding-section wedding-gallery" aria-labelledby="wedding-gallery-heading">
        <div className="wedding-section__inner">
          <p className="wedding-kicker">In action</p>
          <h2 id="wedding-gallery-heading" className="wedding-heading">
            Our wedding Melam on stage
          </h2>
          <p className="wedding-lead">
            Live performances from weddings, processions, and celebrations across Chennai and South India.
          </p>
          <div className="wedding-gallery__mosaic">
            {PERFORMANCE_SHOWCASE.map((item, index) => (
              <figure
                className={`wedding-gallery__item${index === 0 ? " wedding-gallery__item--lead" : ""}`}
                key={item.src}
              >
                <img src={publicImage(item.src)} alt={item.alt} loading="lazy" />
              </figure>
            ))}
          </div>
          <Link to="/gallery" className="wedding-text-link">
            View full gallery
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="wedding-section wedding-steps" aria-labelledby="wedding-steps-heading">
        <div className="wedding-section__inner">
          <p className="wedding-kicker">Booking</p>
          <h2 id="wedding-steps-heading" className="wedding-heading">
            How to book Chenda Melam in Chennai
          </h2>
          <ol className="wedding-steps__list">
            {BOOKING_STEPS.map(({ icon: Icon, title, text }, index) => (
              <li className="wedding-step" key={title}>
                <span className="wedding-step__num" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="wedding-icon" aria-hidden="true">
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="wedding-hero__actions wedding-hero__actions--start">
            <WhatsAppButton>WhatsApp {CONTACT.phone}</WhatsAppButton>
            <a href={`tel:${CONTACT.phoneTel}`} className="wedding-btn wedding-btn--ghost">
              <Phone size={18} aria-hidden="true" />
              {CONTACT.phone}
            </a>
            <a href={`tel:${CONTACT.phone2Tel}`} className="wedding-btn wedding-btn--ghost">
              <Phone size={18} aria-hidden="true" />
              {CONTACT.phone2}
            </a>
          </div>
        </div>
      </section>

      <section className="wedding-section wedding-coverage" aria-labelledby="wedding-coverage-heading">
        <div className="wedding-section__inner">
          <p className="wedding-kicker">Coverage</p>
          <h2 id="wedding-coverage-heading" className="wedding-heading">
            Where we perform
          </h2>
          <p className="wedding-lead">
            Kerala Chenda Melam for wedding across Chennai, Tamil Nadu, Andhra Pradesh, and Telangana.
            Share your date and venue — we confirm availability and pricing quickly.
          </p>
          <ServiceAreasHub />
        </div>
      </section>

      <section className="wedding-section wedding-explore" aria-labelledby="wedding-explore-heading">
        <div className="wedding-section__inner">
          <p className="wedding-kicker">More from us</p>
          <h2 id="wedding-explore-heading" className="wedding-heading">
            Kerala Melam &amp; traditional music
          </h2>
          <p className="wedding-lead">
            Beyond wedding Melam — Panchari Melam, Panchavadyam, fusion collaborations, and classes
            under the same Marar lineage.
          </p>
          <nav className="wedding-explore__links" aria-label="Related pages">
            {EXPLORE_LINKS.map((link) => (
              <Link key={link.to} to={link.to}>
                {link.label}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </div>
      </section>

      <section className="wedding-section wedding-faq" aria-labelledby="wedding-faq-heading">
        <div className="wedding-section__inner wedding-section__inner--narrow">
          <p className="wedding-kicker">FAQ</p>
          <h2 id="wedding-faq-heading" className="wedding-heading">
            Frequently asked questions
          </h2>
          <div className="wedding-faq__list">
            {SEO_FAQ.map((item) => (
              <details className="wedding-faq__item" key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="wedding-cta-band" aria-labelledby="wedding-cta-heading">
        <div className="wedding-cta-band__inner">
          <h2 id="wedding-cta-heading">Ready to book wedding Chenda Melam?</h2>
          <p>
            Tell us your date, venue, and entry moments — we will recommend the right ensemble and send a quote.
          </p>
          <div className="wedding-hero__actions">
            <WhatsAppButton>Book on WhatsApp</WhatsAppButton>
            <Link to={CONTACT_SEARCH} className="wedding-btn wedding-btn--ghost">
              Book Now
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  </>
);

export default ChendaMelamChennai;
